import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import url from '../constants/config';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const STAGES = [
  { key: 'sport_recruitment', label: 'Rekrutacja sportowa', color: '#e0f2fe', border: '#bae6fd', badge: '#0284c7', icon: '🔎' },
  { key: 'after_offer', label: 'Po akceptacji oferty', color: '#fef9c3', border: '#fde68a', badge: '#d97706', icon: '🎓' },
  { key: 'finalization', label: 'Finalizacja procesu', color: '#dcfce7', border: '#bbf7d0', badge: '#16a34a', icon: '✈️' },
];

const AdminPipeline = () => {
  const history = useHistory();
  const [grouped, setGrouped] = useState({ sport_recruitment: [], after_offer: [], finalization: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  /* Drag state */
  const draggingPlayer = useRef(null);
  const draggingFromStage = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const fetchPipeline = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${url}/admin/pipeline`, { headers: getAuthHeader() })
      .then(res => setGrouped(res.data.grouped || {}))
      .catch(err => setError(err.response?.data?.message || 'Nie udało się załadować pipeline.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const updateStage = useCallback(async (playerId, newStage) => {
    try {
      await axios.patch(
        `${url}/admin/pipeline/${playerId}`,
        { stage: newStage },
        { headers: getAuthHeader() }
      );
    } catch {
      // revert on error
      fetchPipeline();
    }
  }, [fetchPipeline]);

  /* Drag & Drop handlers */
  const onDragStart = (player, fromStage) => {
    draggingPlayer.current = player;
    draggingFromStage.current = fromStage;
  };

  const onDragOver = (e, stageKey) => {
    e.preventDefault();
    setDragOver(stageKey);
  };

  const onDragLeave = () => setDragOver(null);

  const onDrop = (e, toStage) => {
    e.preventDefault();
    setDragOver(null);
    const player = draggingPlayer.current;
    const fromStage = draggingFromStage.current;
    if (!player || fromStage === toStage) return;

    // Optimistic update
    setGrouped(prev => {
      const next = { ...prev };
      next[fromStage] = next[fromStage].filter(p => p._id !== player._id);
      next[toStage] = [{ ...player, recruitmentStage: toStage }, ...next[toStage]];
      return next;
    });

    updateStage(player._id, toStage);
    draggingPlayer.current = null;
    draggingFromStage.current = null;
  };

  const filteredGrouped = (stageKey) => {
    const players = grouped[stageKey] || [];
    if (!search.trim()) return players;
    const q = search.toLowerCase();
    return players.filter(p =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
      (p.sport || '').toLowerCase().includes(q)
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: '28px', display: 'block', marginBottom: '12px' }} />
        Ładowanie pipeline...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '32px', color: '#ef4444' }}>
        <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }} />
        {error}
      </div>
    );
  }

  const total = STAGES.reduce((sum, s) => sum + (grouped[s.key]?.length || 0), 0);

  return (
    <div style={{ padding: '24px 20px', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1e293b' }}>
            <i className="fas fa-funnel-dollar" style={{ marginRight: '10px', color: '#2e496b' }} />
            Pipeline rekrutacji
          </h2>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
            {total} zawodników łącznie · przeciągnij kartę aby zmienić etap
          </div>
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Szukaj zawodnika..."
          style={{
            padding: '8px 12px',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            fontSize: '14px',
            width: '200px',
            outline: 'none',
          }}
        />
      </div>

      {/* Kanban board */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${STAGES.length}, minmax(200px, 1fr))`,
          gap: '12px',
          overflowX: 'auto',
        }}
      >
        {STAGES.map(stage => {
          const players = filteredGrouped(stage.key);
          const isOver = dragOver === stage.key;

          return (
            <div
              key={stage.key}
              onDragOver={e => onDragOver(e, stage.key)}
              onDragLeave={onDragLeave}
              onDrop={e => onDrop(e, stage.key)}
              style={{
                background: isOver ? stage.border : '#f8fafc',
                border: `2px solid ${isOver ? stage.badge : '#e2e8f0'}`,
                borderRadius: '12px',
                minHeight: '500px',
                display: 'flex',
                flexDirection: 'column',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              {/* Column header */}
              <div
                style={{
                  background: stage.color,
                  borderBottom: `2px solid ${stage.border}`,
                  borderRadius: '10px 10px 0 0',
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>
                  {stage.icon} {stage.label}
                </div>
                <span
                  style={{
                    background: stage.badge,
                    color: '#fff',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    minWidth: '22px',
                    textAlign: 'center',
                  }}
                >
                  {grouped[stage.key]?.length || 0}
                </span>
              </div>

              {/* Cards */}
              <div style={{ padding: '10px', flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {players.length === 0 ? (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#cbd5e1',
                      fontSize: '13px',
                      minHeight: '80px',
                      borderRadius: '8px',
                      border: isOver ? `2px dashed ${stage.badge}` : '2px dashed transparent',
                    }}
                  >
                    {isOver ? 'Upuść tutaj' : 'Brak zawodników'}
                  </div>
                ) : (
                  players.map(player => (
                    <PipelineCard
                      key={player._id}
                      player={player}
                      stage={stage}
                      onDragStart={onDragStart}
                      onClick={() => history.push(`/available-players/${player._id}`)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PipelineCard = ({ player, stage, onDragStart, onClick }) => {
  const initials = `${(player.firstName || '?')[0]}${(player.lastName || '')[0]}`.toUpperCase();

  return (
    <div
      draggable
      onDragStart={() => onDragStart(player, player.recruitmentStage || 'sport_recruitment')}
      onClick={onClick}
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'grab',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.15s, transform 0.1s',
        userSelect: 'none',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Mini photo bar */}
      <div
        style={{
          height: player.imageBase64 ? '70px' : '44px',
          background: player.imageBase64 ? '#1e293b' : `linear-gradient(135deg, ${stage.color} 0%, ${stage.border} 100%)`,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {player.imageBase64 ? (
          <img
            src={player.imageBase64}
            alt={`${player.firstName} ${player.lastName}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0 }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '14px',
              color: '#1e293b',
            }}
          >
            {initials}
          </div>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b', marginBottom: '2px' }}>
          {player.firstName} {player.lastName}
        </div>
        <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', gap: '8px' }}>
          {player.sport && <span>{player.sport}</span>}
          {player.graduationYear && <span>· {player.graduationYear}</span>}
        </div>
      </div>
    </div>
  );
};

export default AdminPipeline;
